import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    KeyIcon,
    ClipboardDocumentIcon,
    PencilIcon,
    TrashIcon,
    ArrowTopRightOnSquareIcon,
    ShareIcon,
    XMarkIcon,
    EyeIcon,
    EyeSlashIcon,
    ChevronRightIcon,
    ChevronLeftIcon,
    UserIcon,
    FolderIcon,
    HeartIcon,
    ShieldCheckIcon,
    GlobeAltIcon,
    StarIcon,
    LockClosedIcon,
    ExclamationTriangleIcon,
    ChevronDownIcon,
    ArrowDownIcon,
    FunnelIcon,
    ListBulletIcon,
    Squares2X2Icon,
    DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon, StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { useVault } from '../../contexts/VaultContext';
import { IDecryptedPasswordEntry } from '../../types/vault.types';
import EditPasswordModal from './EditPasswordModal';
import { VaultService } from '../../services/vault.service';
import { toast } from 'react-hot-toast';
// import SharePasswordModal from './SharePasswordModal';
import { useLocation } from 'react-router-dom';
import { config } from '../../config';
import { useAppSelector } from '../../hooks/useRedux';
import ConfirmModal from '../common/ConfirmModal';
import { useCategories } from '../../contexts/CategoryContext';

interface PasswordListProps {
    entries?: IDecryptedPasswordEntry[];
    categoryId?: string;
    loading?: boolean;
    error?: string | null;
    showCategoryBadge?: boolean;
    isShared?: boolean;
    permissionLevel?: string;
    onSuccessfulUpdate?: () => Promise<void>;
    sortOption?: 'newest' | 'alphabetical' | 'passwordStrength';
    passwordStrengthFilter?: 'all' | 'veryWeak' | 'weak' | 'fair' | 'strong' | 'veryStrong';
    showSharedOnly?: boolean;
    viewMode?: 'grid' | 'list';
    activeTab?: string;
    hideFavoriteIcon?: boolean;
    isAdminView?: boolean; // New prop to indicate admin viewing mode
}

const EXTENSION_ID = config.EXTENSION_ID;

// Helper function to get stored view mode preference
const getStoredViewMode = (): 'grid' | 'list' => {
    try {
        const stored = localStorage.getItem('passwordListViewMode');
        if (stored === 'grid' || stored === 'list') {
            return stored;
        }
    } catch (error) {
        console.warn('Failed to read view mode preference from localStorage:', error);
    }
    return 'grid'; // Default fallback
};

// Helper function to store view mode preference
const storeViewMode = (mode: 'grid' | 'list') => {
    try {
        localStorage.setItem('passwordListViewMode', mode);
    } catch (error) {
        console.warn('Failed to save view mode preference to localStorage:', error);
    }
};

// Function to get color based on password strength with modern gradient approach
const getStrengthColor = (strength: string): { bg: string; text: string; border: string } => {
    switch (strength) {
        case 'Very Weak':
            return {
                bg: 'bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/30',
                text: 'text-red-700 dark:text-red-400',
                border: 'border-red-200 dark:border-red-700'
            };
        case 'Weak':
            return {
                bg: 'bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/30',
                text: 'text-orange-700 dark:text-orange-400',
                border: 'border-orange-200 dark:border-orange-700'
            };
        case 'Fair':
            return {
                bg: 'bg-gradient-to-r from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/30',
                text: 'text-yellow-700 dark:text-yellow-400',
                border: 'border-yellow-200 dark:border-yellow-700'
            };
        case 'Strong':
            return {
                bg: 'bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30',
                text: 'text-blue-700 dark:text-blue-400',
                border: 'border-blue-200 dark:border-blue-700'
            };
        case 'Very Strong':
            return {
                bg: 'bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/30',
                text: 'text-green-700 dark:text-green-400',
                border: 'border-green-200 dark:border-green-700'
            };
        default:
            return {
                bg: 'bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700',
                text: 'text-gray-600 dark:text-gray-400',
                border: 'border-gray-200 dark:border-gray-600'
            };
    }
};

const renderCategories = (entryCategories: any[], categories: any[]) => {
    if (!entryCategories || entryCategories.length === 0) return null;

    // Filter categories that exist in the user's available categories
    const validCategories = entryCategories.filter(entryCategory =>
        categories.some((userCategory: any) => userCategory.id === entryCategory.id)
    );

    if (validCategories.length === 0) return null;

    return (
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {validCategories.slice(0, 2).map((category, idx) => (
                <div
                    key={category.id}
                    className="inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs 
                             bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/40
                             text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700
                             backdrop-blur-sm shadow-sm flex-shrink-0"
                >
                    <FolderIcon className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate max-w-16 font-medium">{category.name}</span>
                </div>
            ))}

            {validCategories.length > 2 && (
                <div
                    className="inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs 
                             bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700
                             text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600
                             cursor-help relative group/tooltip backdrop-blur-sm shadow-sm flex-shrink-0"
                    tabIndex={0}
                    role="button"
                    aria-haspopup="true"
                    aria-expanded="false"
                >
                    <span className="font-medium">+{validCategories.length - 2}</span>

                    {/* Enhanced Tooltip with better positioning - Only shows on hover of this specific element */}
                    <div
                        className="absolute z-[99999] invisible opacity-0 group-hover/tooltip:visible group-hover/tooltip:opacity-100 
                                 focus-within:visible focus-within:opacity-100 transition-all duration-200 ease-out
                                 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                                 text-gray-800 dark:text-gray-200 text-xs rounded-xl p-3 shadow-2xl 
                                 bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap min-w-[200px]
                                 backdrop-blur-lg pointer-events-none"
                        role="list"
                        aria-label="Additional categories"
                    >
                        {/* Tooltip Arrow */}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-200 dark:border-t-gray-700"></div>
                        
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
                            All Vaults ({validCategories.length})
                        </div>
                        <div className="space-y-1">
                            {validCategories.slice(2).map((cat, idx) => (
                                <div
                                    key={cat.id}
                                    role="listitem"
                                    className="px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700 
                                             focus:bg-gray-50 dark:focus:bg-gray-700 rounded-lg 
                                             transition-colors duration-150 flex items-center space-x-2"
                                >
                                    <FolderIcon className="w-3 h-3 text-primary-500 flex-shrink-0" />
                                    <span className="font-medium truncate">{cat.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const PasswordList: React.FC<PasswordListProps> = ({
    entries = [],
    categoryId,
    loading,
    error,
    showCategoryBadge,
    isShared = false,
    permissionLevel = 'write',
    onSuccessfulUpdate,
    sortOption: propSortOption = 'alphabetical',
    passwordStrengthFilter: propPasswordStrengthFilter = 'all',
    showSharedOnly: propShowSharedOnly = false,
    viewMode: propViewMode = 'grid',
    activeTab = 'passwords',
    hideFavoriteIcon = false,
    isAdminView = false
}) => {
    const { deleteEntry, refreshEntries, searchQuery } = useVault();
    const [selectedEntry, setSelectedEntry] = useState<IDecryptedPasswordEntry | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [shareEntry, setShareEntry] = useState<IDecryptedPasswordEntry | null>(null);
    const location = useLocation();
    const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});
    const [showDetailsPanel, setShowDetailsPanel] = useState<boolean>(false);
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);

    // Sort and Filter States (local management for PasswordList component)
    const [sortOption, setSortOption] = useState<'newest' | 'alphabetical' | 'passwordStrength'>(propSortOption);
    const [passwordStrengthFilter, setPasswordStrengthFilter] = useState<'all' | 'veryWeak' | 'weak' | 'fair' | 'strong' | 'veryStrong'>(propPasswordStrengthFilter);
    const [showSharedOnly, setShowSharedOnly] = useState<boolean>(propShowSharedOnly);
    // Initialize viewMode with stored preference, fallback to propViewMode, then default 'grid'
    const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
        const storedViewMode = getStoredViewMode();
        return propViewMode !== 'grid' ? propViewMode : storedViewMode;
    });

    // Enhanced setViewMode that persists the preference
    const handleViewModeChange = (mode: 'grid' | 'list') => {
        setViewMode(mode);
        storeViewMode(mode);
    };

    // Handle prop changes while preserving user preference
    useEffect(() => {
        // Only update if propViewMode is explicitly different from default and user hasn't stored a preference
        if (propViewMode !== 'grid') {
            const storedViewMode = getStoredViewMode();
            // If no stored preference exists, respect the prop
            if (storedViewMode === 'grid') {
                setViewMode(propViewMode);
            }
        }
    }, [propViewMode]);

    // Sort and Filter Menu States
    const [isSortMenuOpen, setIsSortMenuOpen] = useState<boolean>(false);
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);

    // Temp states for filter changes before applying
    const [tempPasswordStrengthFilter, setTempPasswordStrengthFilter] = useState<'all' | 'veryWeak' | 'weak' | 'fair' | 'strong' | 'veryStrong'>(passwordStrengthFilter);
    const [tempShowSharedOnly, setTempShowSharedOnly] = useState<boolean>(showSharedOnly);

    // Refs for click outside detection
    const sortMenuRef = useRef<HTMLDivElement>(null);
    const filterMenuRef = useRef<HTMLDivElement>(null);
    const { role_id, team_id } = useAppSelector(state => state.auth);
    const { categories, refreshCategories } = useCategories();
    const hasFetchedCategories = useRef(false);

    // Add state for confirm modal
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [actionType, setActionType] = useState<'delete' | 'removeCategory'>('delete');
    const [entryToAction, setEntryToAction] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [passwordPermissionLevel, setPasswordPermissionLevel] = useState<'read' | 'write'>('read');
    const [hoveredEntry, setHoveredEntry] = useState<string | null>(null);

    // Utility functions for sorting and filtering
    const getStrengthValue = (strength: string): number => {
        switch (strength) {
            case 'Very Weak': return 1;
            case 'Weak': return 2;
            case 'Fair': return 3;
            case 'Strong': return 4;
            case 'Very Strong': return 5;
            default: return 0;
        }
    };

    // Reset filters function
    const resetFilters = () => {
        setPasswordStrengthFilter('all');
        setShowSharedOnly(false);
        setTempPasswordStrengthFilter('all');
        setTempShowSharedOnly(false);
    };

    // Apply filters function
    const applyFilters = () => {
        setPasswordStrengthFilter(tempPasswordStrengthFilter);
        setShowSharedOnly(tempShowSharedOnly);
        setIsFilterMenuOpen(false);
    };

    // Click outside handler for menus
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
                setIsSortMenuOpen(false);
            }

            if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
                setIsFilterMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Detect screen size changes
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            if (window.innerWidth >= 768) {
                setShowDetailsPanel(!!selectedEntry);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [selectedEntry]);

    useEffect(() => {
        if (!hasFetchedCategories.current) {
            refreshCategories();
            hasFetchedCategories.current = true; // Set to true after fetching
        }
    }, [refreshCategories]); // Add refreshCategories to dependencies if it changes

    const getFaviconUrl = (websiteUrl: string, faviconUrl: string): string => {
        try {
            if (faviconUrl) {
                return faviconUrl;
            }

            const url = new URL(websiteUrl);
            return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=64`;
        } catch {
            return '/password-manager.png';
        }
    };

    // Enhanced password strength calculation
    const calculatePasswordStrength = (password: string): string => {
        if (!password) return 'Unknown';

        let score = 0;

        // Length check
        if (password.length >= 8) score += 1;
        if (password.length >= 12) score += 1;
        if (password.length >= 16) score += 1;

        // Character diversity
        if (/[a-z]/.test(password)) score += 1;
        if (/[A-Z]/.test(password)) score += 1;
        if (/[0-9]/.test(password)) score += 1;
        if (/[^A-Za-z0-9]/.test(password)) score += 1;

        // Patterns
        if (!/(.)\1{2,}/.test(password)) score += 1; // No repeated characters
        if (!/123|abc|qwe/i.test(password)) score += 1; // No common patterns

        switch (true) {
            case score >= 8: return 'Very Strong';
            case score >= 6: return 'Strong';
            case score >= 4: return 'Fair';
            case score >= 2: return 'Weak';
            default: return 'Very Weak';
        }
    };

    // Get domain from URL for display
    const getDomainFromUrl = (url: string): string => {
        try {
            const domain = new URL(url).hostname;
            return domain.replace('www.', '');
        } catch {
            return url;
        }
    };

    // Enhanced sorting and filtering function
    const getSortedAndFilteredEntries = (entries: IDecryptedPasswordEntry[]): IDecryptedPasswordEntry[] => {
        if (!entries.length) return entries;

        // Apply search filter first
        let filtered = entries.filter(entry => {
            if (!searchQuery) return true;

            const query = searchQuery.toLowerCase();
            return (
                entry.title?.toLowerCase().includes(query) ||
                entry.username?.toLowerCase().includes(query) ||
                entry.website_url?.toLowerCase().includes(query) ||
                entry.notes?.toLowerCase().includes(query)
            );
        });

        // Apply strength filter
        if (passwordStrengthFilter !== 'all') {
            filtered = filtered.filter(entry => {
                const strength = calculatePasswordStrength(entry.password);
                switch (passwordStrengthFilter) {
                    case 'veryWeak':
                        return strength === 'Very Weak';
                    case 'weak':
                        return strength === 'Weak';
                    case 'fair':
                        return strength === 'Fair';
                    case 'strong':
                        return strength === 'Strong';
                    case 'veryStrong':
                        return strength === 'Very Strong';
                    default:
                        return true;
                }
            });
        }

        // Apply shared filter
        if (showSharedOnly) {
            filtered = filtered.filter(entry => entry.shared_key || isShared);
        }

        // Apply sorting
        const sorted = [...filtered];
        switch (sortOption) {
            case 'newest':
                return sorted.sort((a, b) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );
            case 'alphabetical':
                return sorted.sort((a, b) => a.title.localeCompare(b.title));
            case 'passwordStrength': {
                return sorted.sort((a, b) => {
                    const strengthA = getStrengthValue(calculatePasswordStrength(a.password));
                    const strengthB = getStrengthValue(calculatePasswordStrength(b.password));
                    return strengthB - strengthA; // Sort from strongest to weakest
                });
            }
            default:
                return sorted;
        }
    };

    // Apply sorting and filtering to entries before rendering
    const sortedEntries = getSortedAndFilteredEntries(entries);

    // Auto-select first entry in list view
    useEffect(() => {
        if (viewMode === 'list' && sortedEntries.length > 0 && !selectedEntry) {
            setSelectedEntry(sortedEntries[0]);
            // Don't set showDetailsPanel to true in list view - sidebar handles the details
        }
    }, [viewMode, sortedEntries, selectedEntry]);

    const handleCopyItem = async (type: string, item: string) => {
        try {
            await navigator.clipboard.writeText(item);
            if (type === 'password') {
                toast.success('Password copied to clipboard');
            } else {
                toast.success('Username copied to clipboard');
            }
        } catch (err) {
            console.error('Failed to copy password:', err);
            toast.error('Failed to copy password');
        }
    };

    const handleEdit = async (entryId: string, sharedKey: string, permissionLevel: 'read' | 'write') => {
        try {
            // For admin view, use the already decrypted entry data directly
            if (isAdminView) {
                const entry = entries?.find(e => e.id === entryId);
                if (entry) {
                    setSelectedEntry(entry);
                    setPasswordPermissionLevel('read');
                    setIsEditModalOpen(true);
                } else {
                    toast.error('Entry not found');
                }
                return;
            }

            // For normal view, fetch from the backend
            const entry = await VaultService.getEntryById(entryId);
            
            // Preserve the original permission context by finding the original entry
            const originalEntry = entries?.find(e => e.id === entryId);
            if (originalEntry) {
                // Merge fresh data with original permission context
                const mergedEntry = {
                    ...entry,
                    permission_level: originalEntry.permission_level,
                    is_owner: originalEntry.is_owner
                };
                setSelectedEntry(mergedEntry);
            } else {
                setSelectedEntry(entry);
            }
            
            setPasswordPermissionLevel(permissionLevel);
            setIsEditModalOpen(true);
        } catch (err) {
            console.error('Failed to fetch entry:', err);
            toast.error('Failed to fetch entry');
        }
    };

    const handleClone = async (entry: IDecryptedPasswordEntry) => {
        try {
            if (!isAdminView) {
                toast.error('Clone feature is only available in admin view');
                return;
            }

            // Confirm clone action
            if (!window.confirm(`Clone this password entry to your own vault?\n\nWebsite: ${entry.website_url}\nUsername: ${entry.username}`)) {
                return;
            }

            // The entry is already decrypted by admin's ORG key
            // Now we create a new entry encrypted with admin's own vault key
            const newEntry = {
                title: entry.title,
                website_url: entry.website_url,
                username: entry.username,
                password: entry.password,
                notes: entry.notes,
                favourite: false, // Don't copy favorite status
                password_strength: entry.password_strength
                
            };
            const workspaceId = team_id || undefined;

            // Save as a new entry in admin's vault (will be encrypted with admin's vault key)
            await VaultService.createEntry(newEntry, workspaceId);
            
            toast.success('Password cloned to your vault successfully');
            
            // Refresh the admin's own entries if needed
            if (refreshEntries) {
                refreshEntries();
            }
        } catch (err) {
            console.error('Failed to clone entry:', err);
            toast.error('Failed to clone entry');
        }
    };

    const handleWebsiteLaunch = async (entry: IDecryptedPasswordEntry) => {
        try {
            // Store both the active entry and indicate it was launched
            if (chrome?.runtime && EXTENSION_ID) {
                chrome.runtime.sendMessage(
                    EXTENSION_ID,
                    {
                        type: 'SET_LAUNCH_DATA',
                        payload: {
                            launchedEntryId: entry.id,
                            url: entry.website_url,
                            timestamp: Date.now()
                        }
                    },
                    (response) => {
                        if (entry.website_url) {
                            window.open(entry.website_url, '_blank');
                            toast.success('Website opened');
                        } else {
                            toast.error('No website URL provided');
                        }
                    }
                );
            } else {
                // Fallback
                if (entry.website_url) {
                    window.open(entry.website_url, '_blank');
                }
            }
        } catch (err) {
            console.error('Failed to launch website:', err);
            toast.error('Failed to launch website');
        }
    };

    const handleShare = (entry: IDecryptedPasswordEntry) => {
        setShareEntry(entry);
        setIsShareModalOpen(true);
    };

    const handleRemoveFromCategory = async (entryId: string) => {
        setEntryToAction(entryId);
        setActionType('removeCategory');
        setShowConfirmModal(true);
    };

    const handleDeleteClick = (entryId: string) => {
        setEntryToAction(entryId);
        setActionType('delete');
        setShowConfirmModal(true);
    };

    const handleConfirmAction = async () => {
        try {
            setIsProcessing(true);
            if (actionType === 'delete') {
                // Check if the entry is shared and pass that info to the deleteEntry function
                const isEntryShared = entries.find(e => e.id === entryToAction)?.shared_key ? true : false;
                const sharedKey = entries.find(e => e.id === entryToAction)?.shared_key;

                await deleteEntry(entryToAction, isEntryShared, sharedKey);

                if (selectedEntry?.id === entryToAction) {
                    setSelectedEntry(null);
                    closeDetailsPanel();
                }

                // Call the update callback to refresh the parent component's data
                if (onSuccessfulUpdate) {
                    await onSuccessfulUpdate();
                }
                await refreshEntries();

                toast.success('Password deleted successfully');
            } else if (actionType === 'removeCategory') {
                await VaultService.removeCategoryFromEntry(entryToAction);
                await refreshEntries();
                toast.success('Password removed from category');
            }
        } catch (err) {
            console.error('Failed to perform action:', err);
            toast.error(actionType === 'delete' ? 'Failed to delete entry' : 'Failed to remove password from category');
        } finally {
            setIsProcessing(false);
            setShowConfirmModal(false);
            setEntryToAction('');
        }
    };

    const toggleDetailsPanel = (entryId: string) => {
        if (selectedEntry?.id === entryId) {
            setSelectedEntry(null);
            setShowDetailsPanel(false);
        } else {
            const entry = entries?.find(e => e.id === entryId);
            if (entry) {
                setSelectedEntry(entry);
                setShowDetailsPanel(true);
            }
        }
    };

    const closeDetailsPanel = () => {
        setShowDetailsPanel(false);
        if (isMobile) {
            setSelectedEntry(null);
        }
    };

    // Sort menu component
    const SortMenu = () => {
        return (
            <div className="relative z-50" ref={sortMenuRef}>
                {/* Sort button */}
                <button
                    onClick={() => {
                        setIsSortMenuOpen(!isSortMenuOpen);
                        setIsFilterMenuOpen(false);
                    }}
                    className="flex items-center px-2 sm:px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium text-white hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                    <ArrowDownIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span className="">Sort</span>
                    <ChevronDownIcon className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
                </button>

                {/* Dropdown */}
                {isSortMenuOpen && (
                    <div className="absolute mt-2 left-0 sm:right-0 sm:left-auto w-40 sm:w-48 max-w-[90vw] bg-white shadow-xl rounded-md overflow-hidden z-[99999] border border-gray-200 max-h-60 overflow-y-auto">
                        <div className="py-1">
                            {/* Newest First */}
                            <button
                                className={`flex items-center justify-between w-full px-3 py-2 text-left text-xs sm:text-sm rounded-md ${sortOption === "newest"
                                        ? "bg-primary-50 text-primary-700"
                                        : "hover:bg-gray-50"
                                    }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSortOption("newest");
                                    setIsSortMenuOpen(false);
                                }}
                            >
                                <span>Newest First</span>
                                {sortOption === "newest" && (
                                    <ArrowDownIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                                )}
                            </button>

                            {/* Alphabetical */}
                            <button
                                className={`flex items-center justify-between w-full px-3 py-2 text-left text-xs sm:text-sm rounded-md ${sortOption === "alphabetical"
                                        ? "bg-primary-50 text-primary-700"
                                        : "hover:bg-gray-50"
                                    }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSortOption("alphabetical");
                                    setIsSortMenuOpen(false);
                                }}
                            >
                                <span>Alphabetical</span>
                                {sortOption === "alphabetical" && (
                                    <ArrowDownIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                                )}
                            </button>

                            {/* Password Strength (only for passwords tab) */}
                            {activeTab === "passwords" && (
                                <button
                                    className={`flex items-center justify-between w-full px-3 py-2 text-left text-xs sm:text-sm rounded-md ${sortOption === "passwordStrength"
                                            ? "bg-primary-50 text-primary-700"
                                            : "hover:bg-gray-50"
                                        }`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSortOption("passwordStrength");
                                        setIsSortMenuOpen(false);
                                    }}
                                >
                                    <span className="truncate">Password Strength</span>
                                    {sortOption === "passwordStrength" && (
                                        <ArrowDownIcon className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Filter menu component
    const FilterMenu = () => {
        // Get the current tab's label
        const getTabLabel = () => {
            switch (activeTab) {
                case 'passwords': return 'passwords';
                case 'cardDetails': return 'cards';
                case 'bankDetails': return 'bank accounts';
                case 'personalDetails': return 'personal details';
                default: return 'items';
            }
        };

        return (
            <div className="relative z-50" ref={filterMenuRef}>
                <button
                    onClick={() => {
                        setIsFilterMenuOpen(!isFilterMenuOpen);
                        setIsSortMenuOpen(false);
                        // Initialize temporary filter state with current filter values
                        setTempPasswordStrengthFilter(passwordStrengthFilter);
                        setTempShowSharedOnly(showSharedOnly);
                    }}
                    className="flex items-center px-2 sm:px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium text-white hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                    <FunnelIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span className="">Filter</span>
                    {(passwordStrengthFilter !== 'all' || showSharedOnly) && (
                        <span className="ml-1 px-1.5 py-0.5 bg-primary-100 text-primary-800 text-xs rounded-full">
                            {(passwordStrengthFilter !== 'all' && showSharedOnly) ? '2' : '1'}
                        </span>
                    )}
                    <ChevronDownIcon className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
                </button>

                {isFilterMenuOpen && (
                    <div className="absolute mt-2 right-0 w-64 sm:w-72 bg-white shadow-xl rounded-md overflow-hidden z-[99999] border border-gray-200 max-h-80 overflow-y-auto">
                        <div className="flex justify-between items-center p-3 border-b border-gray-200">
                            <span className="font-medium text-xs sm:text-sm">Filter {getTabLabel()}</span>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    resetFilters();
                                }}
                                className="text-xs sm:text-sm text-primary-600 hover:text-primary-800"
                            >
                                Reset
                            </button>
                        </div>

                        {/* Filter Options */}
                        <div className="p-3">
                            <div className="space-y-2">
                                <div className="flex items-center">
                                    <input
                                        id="sharedOnly"
                                        type="checkbox"
                                        checked={tempShowSharedOnly}
                                        onChange={(e) => setTempShowSharedOnly(e.target.checked)}
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="sharedOnly" className="ml-2 block text-sm text-gray-700">
                                        Shared with me only
                                    </label>
                                </div>

                                {/* Password Strength Filter - Only show for Passwords tab */}
                                {activeTab === 'passwords' && (
                                    <div className="mt-3">
                                        <h4 className="text-xs font-medium text-gray-500 mb-2">Password Strength</h4>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'all' ? 'bg-primary-50 text-primary-700 border border-primary-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setTempPasswordStrengthFilter('all');
                                                }}
                                            >
                                                All Strengths
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'veryWeak' ? 'border border-red-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'veryWeak' ? 'Very Weak' : '').bg}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setTempPasswordStrengthFilter('veryWeak');
                                                }}
                                            >
                                                Very Weak
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'weak' ? 'border border-orange-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'weak' ? 'Weak' : '').bg}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setTempPasswordStrengthFilter('weak');
                                                }}
                                            >
                                                Weak
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'fair' ? 'border border-yellow-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'fair' ? 'Fair' : '').bg}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setTempPasswordStrengthFilter('fair');
                                                }}
                                            >
                                                Fair
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'strong' ? 'border border-blue-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'strong' ? 'Strong' : '').bg}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setTempPasswordStrengthFilter('strong');
                                                }}
                                            >
                                                Strong
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'veryStrong' ? 'border border-green-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'veryStrong' ? 'Very Strong' : '').bg}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setTempPasswordStrengthFilter('veryStrong');
                                                }}
                                            >
                                                Very Strong
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-3 border-t border-gray-200">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    applyFilters();
                                }}
                                className="w-full py-2 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Enhanced password strength indicator component
    const PasswordStrengthIndicator = ({ strength, showLabel = true }: { strength: string; showLabel?: boolean }) => {
        const colors = getStrengthColor(strength);

        return (
            <div className="flex items-center space-x-2">
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text} ${colors.border} border backdrop-blur-sm`}>
                    <LockClosedIcon className="w-3 h-3 mr-1" />
                    {showLabel && <span>{strength}</span>}
                </div>

                {/* Visual strength bars */}
                <div className="flex space-x-0.5">
                    {[1, 2, 3, 4, 5].map((level) => {
                        const strengthLevel = {
                            'Very Weak': 1, 'Weak': 2, 'Fair': 3, 'Strong': 4, 'Very Strong': 5, 'Unknown': 0
                        }[strength] || 0;

                        return (
                            <div
                                key={level}
                                className={`w-1 h-3 rounded-full transition-all duration-300 ${level <= strengthLevel
                                    ? colors.text.includes('red') ? 'bg-red-400' :
                                        colors.text.includes('orange') ? 'bg-orange-400' :
                                            colors.text.includes('yellow') ? 'bg-yellow-400' :
                                                colors.text.includes('blue') ? 'bg-blue-400' :
                                                    colors.text.includes('green') ? 'bg-green-400' : 'bg-gray-300'
                                    : 'bg-gray-200 dark:bg-gray-600'
                                    }`}
                            />
                        );
                    })}
                </div>
            </div>
        );
    };

    // Modern Password Card Component
    const PasswordCard = ({ entry, isSelected, onClick }: {
        entry: IDecryptedPasswordEntry;
        isSelected: boolean;
        onClick: () => void;
    }) => {
        const strength = calculatePasswordStrength(entry.password);
        const isHovered = hoveredEntry === entry.id;
        const isFavorite = entry.favourite;

        return (
            <div
                className={`group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl
                    ${isSelected
                        ? 'bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/40 border-2 border-primary-300 dark:border-primary-600 shadow-lg'
                        : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 shadow-sm hover:shadow-lg'
                    }`}
                onClick={onClick}
                onMouseEnter={() => setHoveredEntry(entry.id)}
                onMouseLeave={() => setHoveredEntry(null)}
            >
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary-500 to-primary-600"></div>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-16 translate-x-16"></div>
                </div>

                {/* Content */}
                <div className="relative p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                            {/* Favicon */}
                            <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 
                                          dark:from-gray-700 dark:to-gray-800 flex items-center justify-center overflow-hidden
                                          shadow-sm border border-gray-200 dark:border-gray-600 flex-shrink-0">
                                {entry.favicon_url || entry.website_url ? (
                                    <img
                                        src={getFaviconUrl(entry.website_url || '', entry.favicon_url || '')}
                                        alt={`${entry.title} favicon`}
                                        className="w-8 h-8 rounded-lg object-cover"
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.style.display = 'none';
                                            target.nextElementSibling?.classList.remove('hidden');
                                        }}
                                    />
                                ) : null}
                                <GlobeAltIcon className={`w-6 h-6 text-gray-400 ${entry.favicon_url || entry.website_url ? 'hidden' : ''}`} />
                            </div>

                            {/* Title and URL */}
                            <div className="flex-1 min-w-0">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                    {entry.title}
                                </h3>
                                {entry.website_url && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                        {getDomainFromUrl(entry.website_url)}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Favorite Icon */}
                        {isFavorite && !hideFavoriteIcon && (
                            <div className="flex-shrink-0">
                                <StarSolidIcon className="w-5 h-5 text-yellow-400" />
                            </div>
                        )}
                    </div>

                    {/* Username */}
                    {entry.username && (
                        <div className="flex items-center space-x-2 mb-3">
                            <UserIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-sm text-gray-600 dark:text-gray-300 truncate font-medium">
                                {entry.username}
                            </span>
                        </div>
                    )}

                    {/* Password Strength */}
                    <div className="mb-4">
                        <PasswordStrengthIndicator strength={strength} showLabel={true} />
                    </div>

                    {/* Categories */}
                    {renderCategories(entry.categories || [], categories)}

                    {/* Actions - Show on Hover */}
                    {/* <div className={`absolute top-4 right-4 flex space-x-1 transition-all duration-200 ${
                        isHovered || isSelected ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                    }`}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCopyItem('username', entry.username);
                            }}
                            className="p-2 bg-white dark:bg-gray-700 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 
                                     hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors group/btn"
                            title="Copy Username"
                        >
                            <UserIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover/btn:text-primary-600 dark:group-hover/btn:text-primary-400" />
                        </button>
                        
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCopyItem('password', entry.password);
                            }}
                            className="p-2 bg-white dark:bg-gray-700 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 
                                     hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors group/btn"
                            title="Copy Password"
                        >
                            <KeyIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover/btn:text-primary-600 dark:group-hover/btn:text-primary-400" />
                        </button>
                        
                        {entry.website_url && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleWebsiteLaunch(entry);
                                }}
                                className="p-2 bg-white dark:bg-gray-700 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 
                                         hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors group/btn"
                                title="Open Website"
                            >
                                <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover/btn:text-primary-600 dark:group-hover/btn:text-primary-400" />
                            </button>
                        )}
                    </div> */}

                    {/* Hover Overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-r from-primary-500/10 to-primary-600/10 rounded-2xl transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'
                        }`} />
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-12 min-h-[400px]">
                <div className="relative">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
                    <div className="absolute inset-0 animate-pulse">
                        <LockClosedIcon className="w-6 h-6 text-primary-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                    </div>
                </div>
                <p className="mt-4 text-gray-500 dark:text-gray-400 font-medium">Loading your vault...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 m-4 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/30 
                          border border-red-200 dark:border-red-700 rounded-2xl">
                <div className="flex items-center space-x-3">
                    <div className="p-2 bg-red-100 dark:bg-red-800/50 rounded-lg">
                        <XMarkIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-red-800 dark:text-red-300">Error Loading Passwords</h3>
                        <p className="text-red-700 dark:text-red-400">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    // if (sortedEntries.length === 0) {
    //     return (
    //         <div className="text-center py-16 px-6">
    //             <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 
    //                           rounded-2xl flex items-center justify-center mb-6 shadow-lg">
    //                 <KeyIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
    //             </div>
    //             <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">No passwords found</h3>
    //             <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
    //                 {searchQuery 
    //                     ? "No passwords match your search criteria. Try adjusting your search terms."
    //                     : "Get started by creating your first password entry to secure your digital life."
    //                 }
    //             </p>
    //             {!searchQuery && (
    //                 <button className="mt-6 inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 
    //                                  text-white font-medium rounded-xl hover:from-primary-700 hover:to-primary-800 
    //                                  transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105">
    //                     <KeyIcon className="w-5 h-5 mr-2" />
    //                     Add Your First Password
    //                 </button>
    //             )}
    //         </div>
    //     );
    // }

    // Mobile view - either show list or details
    if (isMobile) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                {/* Mobile Header */}
                <div className="sticky top-0 z-40 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 backdrop-blur-sm border-b border-primary-300 dark:border-primary-700 px-4 py-4 shadow-lg">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-0 right-0 w-32 h-16 bg-white rounded-full -translate-y-8 translate-x-16"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-12 bg-white rounded-full translate-y-6 -translate-x-12"></div>
                    </div>

                    <div className="relative flex flex-col space-y-4">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 shadow-lg">
                                <KeyIcon className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-white">Password Details</h1>
                                <p className="text-white/70 text-xs">Manage your password information</p>
                            </div>
                        </div>

                        {/* Count Display */}
                        <div className="flex items-center space-x-3 px-3 py-2 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                            <div className="p-1.5 bg-white/20 rounded-lg">
                                <KeyIcon className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <div className="text-lg font-bold text-white">
                                    {sortedEntries.length}
                                </div>
                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                    {sortedEntries.length === 1 ? 'Password' : 'Passwords'}
                                </div>
                            </div>
                        </div>

                        {/* Mobile Controls */}
                        <div className="flex items-center justify-between space-x-2">
                            {/* Sort Menu */}
                            <SortMenu />

                            {/* Filter Menu */}
                            <FilterMenu />

                            {/* View Toggle - Hidden on Mobile */}
                            <div className="hidden sm:flex items-center bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl p-1 shadow-sm border border-white/30">
                                <button
                                    onClick={() => handleViewModeChange('grid')}
                                    className={`p-1.5 sm:p-2.5 rounded-lg transition-all duration-200 ${viewMode === 'grid'
                                        ? 'bg-white text-primary-600 shadow-md scale-105'
                                        : 'text-white/80 hover:text-white hover:bg-white/20'
                                        }`}
                                    title="Grid View"
                                >
                                    <Squares2X2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                </button>
                                <button
                                    onClick={() => handleViewModeChange('list')}
                                    className={`p-1.5 sm:p-2.5 rounded-lg transition-all duration-200 ${viewMode === 'list'
                                        ? 'bg-white text-primary-600 shadow-md scale-105'
                                        : 'text-white/80 hover:text-white hover:bg-white/20'
                                        }`}
                                    title="List View"
                                >
                                    <ListBulletIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {!showDetailsPanel ? (
                    // Mobile Password List
                    <div className="p-4">
                        {sortedEntries.length === 0 && (
                            <div className="text-center py-16 px-6">
                                <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 
                                          rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                                    <KeyIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">No passwords found</h3>
                                <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                                    {searchQuery
                                        ? "No passwords match your search criteria. Try adjusting your search terms."
                                        : "Get started by creating your first password entry to secure your digital life."
                                    }
                                </p>
                            </div>
                        )}

                        <div className="space-y-3">
                            {sortedEntries?.map((entry: IDecryptedPasswordEntry) => {
                                const strength = calculatePasswordStrength(entry.password);
                                return (
                                    <div
                                        key={entry.id}
                                        className={`p-4 rounded-xl cursor-pointer transition-all duration-200 shadow-sm border
                                            ${selectedEntry?.id === entry.id
                                                ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-700'
                                                : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-gray-200 dark:border-gray-700'
                                            }`}
                                        onClick={() => toggleDetailsPanel(entry.id)}
                                    >
                                        <div className="flex items-start space-x-4">
                                            {/* Favicon */}
                                            <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 
                                                dark:from-gray-700 dark:to-gray-800 flex items-center justify-center overflow-hidden
                                                shadow-sm border border-gray-200 dark:border-gray-600 flex-shrink-0">
                                                {entry.website_url || entry.favicon_url ? (
                                                    <img
                                                        src={getFaviconUrl(entry?.website_url || '', entry?.favicon_url || '')}
                                                        alt=""
                                                        className="w-7 h-7 rounded"
                                                        onError={(e) => {
                                                            const target = e.target as HTMLImageElement;
                                                            target.src = '/default-favicon.png';
                                                        }}
                                                    />
                                                ) : (
                                                    <KeyIcon className="w-6 h-6 text-gray-400" />
                                                )}
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                {/* Title and Favorite */}
                                                <div className="flex items-center justify-between mb-2">
                                                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                                                        {entry.title}
                                                    </h3>
                                                    <div className="flex items-center space-x-2 flex-shrink-0">
                                                        {entry.favourite && !hideFavoriteIcon && (
                                                            <StarSolidIcon className="w-5 h-5 text-yellow-400" />
                                                        )}
                                                        <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                                                    </div>
                                                </div>

                                                {/* Username */}
                                                {entry.username && (
                                                    <div className="flex items-center space-x-2 mb-2">
                                                        <UserIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                        <span className="text-sm text-gray-600 dark:text-gray-300 truncate">
                                                            {entry.username}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Website */}
                                                {entry.website_url && (
                                                    <div className="flex items-center space-x-2 mb-2">
                                                        <GlobeAltIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                        <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                            {getDomainFromUrl(entry.website_url)}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Password Strength */}
                                                <div className="mb-2">
                                                    <PasswordStrengthIndicator strength={strength} showLabel={true} />
                                                </div>

                                                {/* Categories */}
                                                {entry.categories && entry.categories.length > 0 && (
                                                    <div className="mt-2">
                                                        {renderCategories(entry.categories, categories)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    // Mobile Password Details
                    <div className="min-h-screen bg-white dark:bg-gray-800">
                        {/* Mobile Details Header */}
                        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 shadow-sm">
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={closeDetailsPanel}
                                    className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <ChevronLeftIcon className="h-5 w-5" />
                                </button>
                                <div className="flex items-center space-x-3 flex-1">
                                    {/* Favicon */}
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 
                                        dark:from-gray-700 dark:to-gray-800 flex items-center justify-center overflow-hidden
                                        shadow-sm border border-gray-200 dark:border-gray-600">
                                        {selectedEntry?.website_url || selectedEntry?.favicon_url ? (
                                            <img
                                                src={getFaviconUrl(selectedEntry?.website_url || '', selectedEntry?.favicon_url || '')}
                                                alt=""
                                                className="w-6 h-6 rounded"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = '/default-favicon.png';
                                                }}
                                            />
                                        ) : (
                                            <KeyIcon className="w-5 h-5 text-gray-400" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                                            {selectedEntry?.title}
                                        </h2>
                                        {selectedEntry?.website_url && (
                                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                {getDomainFromUrl(selectedEntry.website_url)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-4 space-y-6">
                            {/* Website Field */}
                            {selectedEntry?.website_url && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Website</label>
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="text"
                                            value={selectedEntry?.website_url || ''}
                                            readOnly
                                            className="flex-1 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600"
                                        />
                                        <button
                                            onClick={() => selectedEntry?.website_url && handleWebsiteLaunch(selectedEntry)}
                                            className="p-2.5 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                        >
                                            <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Username Field */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Username</label>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="text"
                                        value={selectedEntry?.username || ''}
                                        readOnly
                                        className="flex-1 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600"
                                    />
                                    <button
                                        onClick={() => selectedEntry && handleCopyItem('username', selectedEntry.username)}
                                        className="p-2.5 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <ClipboardDocumentIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Password Field */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type={selectedEntry && showPassword[selectedEntry.id] ? 'text' : 'password'}
                                        value={selectedEntry?.password || ''}
                                        readOnly
                                        className="flex-1 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600"
                                    />
                                    <button
                                        onClick={() => {
                                            if (selectedEntry) {
                                                setShowPassword(prev => ({
                                                    ...prev,
                                                    [selectedEntry.id]: !prev[selectedEntry.id]
                                                }));
                                            }
                                        }}
                                        className="p-2.5 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        {selectedEntry && showPassword[selectedEntry.id] ? (
                                            <EyeSlashIcon className="w-5 h-5" />
                                        ) : (
                                            <EyeIcon className="w-5 h-5" />
                                        )}
                                    </button>
                                    <button
                                        onClick={() => selectedEntry && handleCopyItem('password', selectedEntry.password)}
                                        className="p-2.5 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <ClipboardDocumentIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Password Strength */}
                            {selectedEntry && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Password Strength</label>
                                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <PasswordStrengthIndicator
                                            strength={calculatePasswordStrength(selectedEntry.password)}
                                            showLabel={true}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Notes Field */}
                            {selectedEntry?.notes && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                                    <textarea
                                        value={selectedEntry.notes}
                                        readOnly
                                        rows={3}
                                        className="w-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 resize-none"
                                    />
                                </div>
                            )}

                            {/* Categories */}
                            {selectedEntry?.categories && selectedEntry.categories.length > 0 && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Vaults</label>
                                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        {renderCategories(selectedEntry.categories, categories)}
                                    </div>
                                </div>
                            )}

                            {/* Mobile Action Buttons */}
                            <div className="pt-4 space-y-3">
                                {!selectedEntry?.is_owner && selectedEntry?.permission_level === 'read' && role_id !== 1 && role_id !== 2 ? (
                                    <button
                                        onClick={() => selectedEntry && handleEdit(selectedEntry.id, selectedEntry?.shared_key || '', 'read')}
                                        className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center space-x-2 shadow-sm"
                                    >
                                        <EyeIcon className="h-5 w-5" />
                                        <span>View Details</span>
                                    </button>
                                ) : (
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={() => selectedEntry && handleEdit(selectedEntry.id, selectedEntry?.shared_key || '', 'write')}
                                            className="py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center space-x-2 shadow-sm"
                                        >
                                            <PencilIcon className="h-4 w-4" />
                                            <span>Edit</span>
                                        </button>
                                        {((selectedEntry?.is_owner) || (!selectedEntry?.is_owner)) && (
                                            <button
                                                onClick={() => selectedEntry && handleDeleteClick(selectedEntry.id)}
                                                className="py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center space-x-2 shadow-sm"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                                <span>Delete</span>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {selectedEntry && isEditModalOpen && (
                    <EditPasswordModal
                        entry={selectedEntry}
                        isOpen={isEditModalOpen}
                        onClose={() => {
                            setIsEditModalOpen(false);
                            closeDetailsPanel();
                        }}
                        isSharedPassword={selectedEntry.shared_key ? true : isShared}
                        permissionLevel={selectedEntry.permission_level || permissionLevel}
                        onSuccessfulUpdate={onSuccessfulUpdate}
                    />
                )}
            </div>
        );
    }

    // Desktop view - Modern Password Manager Interface
    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header Controls */}
            <div className="sticky top-0 z-40 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 backdrop-blur-sm border-b border-primary-300 dark:border-primary-700 px-6 py-6 shadow-lg">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-64 h-32 bg-white rounded-full -translate-y-16 translate-x-32"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-24 bg-white rounded-full translate-y-12 -translate-x-24"></div>
                </div>

                <div className="relative flex flex-col gap-6 sm:flex-row sm:flex-wrap sm:items-center lg:justify-between">
                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4 sm:gap-6 mb-4 sm:mb-0">
                        {/* Title and Icon for Desktop */}
                        <div className="hidden sm:flex items-center space-x-3">
                            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30 shadow-lg">
                                <KeyIcon className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-white">Password Details</h1>
                                <p className="text-white/70 text-sm">Manage your password information</p>
                            </div>
                        </div>

                        {/* Mobile Header */}
                        <div className="sm:hidden">
                            <div className="flex items-center space-x-3 mb-3">
                                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 shadow-lg">
                                    <KeyIcon className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-white">Password Details</h1>
                                    <p className="text-white/70 text-xs">Manage your password information</p>
                                </div>
                            </div>
                        </div>

                        {/* Count Display */}
                        <div className="flex items-center space-x-3 px-3 sm:px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/30 flex-shrink-0 min-w-[160px]">
                            <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg">
                                <KeyIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </div>
                            <div>
                                <div className="text-lg sm:text-2xl font-bold text-white">
                                    {sortedEntries.length}
                                </div>
                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                    {sortedEntries.length === 1 ? 'Password' : 'Passwords'}
                                </div>
                            </div>
                        </div>

                        {/* Strong Passwords Count - Desktop Only */}
                        {/* <div className="hidden sm:flex items-center space-x-3 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                            <div className="p-2 bg-green-500/30 rounded-lg">
                                <LockClosedIcon className="w-4 h-4 text-green-200" />
                            </div>
                            <div>
                                <div className="text-lg font-semibold text-white">
                                    {sortedEntries.filter(entry => {
                                        const strength = calculatePasswordStrength(entry.password);
                                        return strength === 'Very Strong' || strength === 'Strong';
                                    }).length}
                                </div>
                                <div className="text-xs text-white/70 uppercase tracking-wide">
                                    Strong
                                </div>
                            </div>
                        </div> */}

                        {/* Weak Passwords Count - Desktop Only */}
                        {/* <div className="hidden sm:flex items-center space-x-3 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                            <div className="p-2 bg-red-500/30 rounded-lg">
                                <ExclamationTriangleIcon className="w-4 h-4 text-red-200" />
                            </div>
                            <div>
                                <div className="text-lg font-semibold text-white">
                                    {sortedEntries.filter(entry => {
                                        const strength = calculatePasswordStrength(entry.password);
                                        return strength === 'Very Weak' || strength === 'Weak';
                                    }).length}
                                </div>
                                <div className="text-xs text-white/70 uppercase tracking-wide">
                                    Weak
                                </div>
                            </div>
                        </div> */}
                    </div>

                    <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto sm:justify-end">
                        {/* Sort Menu */}
                        <div className="flex-1 min-w-[180px] sm:flex-none sm:min-w-0">
                            <SortMenu />
                        </div>

                        {/* Filter Menu */}
                        <div className="flex-1 min-w-[180px] sm:flex-none sm:min-w-0">
                            <FilterMenu />
                        </div>

                        {/* View Toggle */}
                        <div className="hidden sm:flex items-center bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl p-1 shadow-sm border border-white/30 flex-shrink-0">
                            <button
                                onClick={() => handleViewModeChange('grid')}
                                className={`p-1.5 sm:p-2.5 rounded-lg transition-all duration-200 ${viewMode === 'grid'
                                    ? 'bg-white text-primary-600 shadow-md scale-105'
                                    : 'text-white/80 hover:text-white hover:bg-white/20'
                                    }`}
                                title="Grid View"
                            >
                                <Squares2X2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                            <button
                                onClick={() => handleViewModeChange('list')}
                                className={`p-1.5 sm:p-2.5 rounded-lg transition-all duration-200 ${viewMode === 'list'
                                    ? 'bg-white text-primary-600 shadow-md scale-105'
                                    : 'text-white/80 hover:text-white hover:bg-white/20'
                                    }`}
                                title="List View"
                            >
                                <ListBulletIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {sortedEntries.length === 0 && (
                <div className="text-center py-16 px-6">
                    <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 
                              rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                        <KeyIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">No passwords found</h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                        {searchQuery
                            ? "No passwords match your search criteria. Try adjusting your search terms."
                            : "Get started by creating your first password entry to secure your digital life."
                        }
                    </p>
                    {/* {!searchQuery && (
                        <button className="mt-6 inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 
                                     text-white font-medium rounded-xl hover:from-primary-700 hover:to-primary-800 
                                     transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105">
                            <KeyIcon className="w-5 h-5 mr-2" />
                            Add Your First Password
                        </button>
                    )} */}
                </div>
            )}

            {sortedEntries.length > 0 && (
                <>
                    {/* Main Content */}
                    <div className="p-6">
                        {viewMode === 'grid' ? (
                            /* Grid View */
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                                {sortedEntries.map((entry) => (
                                    <PasswordCard
                                        key={entry.id}
                                        entry={entry}
                                        isSelected={selectedEntry?.id === entry.id}
                                        onClick={() => toggleDetailsPanel(entry.id)}
                                    />
                                ))}
                            </div>
                        ) : (
                            /* List View with Sidebar */
                            <div className="flex h-[calc(100vh-180px)] bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                {/* Left Panel - Password List */}
                                <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
                                    <div className="p-4 space-y-3">
                                        {sortedEntries.map((entry) => (
                                            <div
                                                key={entry.id}
                                                className="relative group"
                                                onMouseEnter={() => setHoveredEntry(entry.id)}
                                                onMouseLeave={() => setHoveredEntry(null)}
                                            >
                                                <div
                                                    className={`relative p-6 bg-white dark:bg-gray-800 rounded-2xl border-2 transition-all duration-300 cursor-pointer overflow-hidden
                                                ${selectedEntry?.id === entry.id
                                                            ? 'border-primary-300 dark:border-primary-600 bg-primary-50/50 dark:bg-primary-900/20 shadow-lg shadow-primary-100 dark:shadow-primary-900/30'
                                                            : 'border-gray-200 dark:border-gray-700 hover:border-primary-200 dark:hover:border-primary-700 hover:shadow-lg dark:hover:shadow-gray-900/50'
                                                        }`}
                                                    onClick={() => {
                                                        setSelectedEntry(entry);
                                                        // In list view, details are shown in sidebar, not modal
                                                        if (viewMode !== 'list') {
                                                            setShowDetailsPanel(true);
                                                        }
                                                    }}
                                                >
                                                    <div className="flex items-center space-x-5">
                                                        {/* Favicon */}
                                                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 
                                                              dark:from-gray-700 dark:to-gray-800 flex items-center justify-center overflow-hidden
                                                              shadow-lg border border-gray-200 dark:border-gray-600 flex-shrink-0 group-hover:shadow-xl transition-shadow duration-300">
                                                            {entry.favicon_url || entry.website_url ? (
                                                                <img
                                                                    src={getFaviconUrl(entry?.website_url || '', entry.favicon_url || '')}
                                                                    alt={`${entry.title} favicon`}
                                                                    className="w-8 h-8 rounded-xl object-cover"
                                                                    onError={(e) => {
                                                                        const target = e.target as HTMLImageElement;
                                                                        target.style.display = 'none';
                                                                        target.nextElementSibling?.classList.remove('hidden');
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <GlobeAltIcon className={`w-7 h-7 text-gray-400 ${entry.favicon_url || entry.website_url ? 'hidden' : ''}`} />
                                                        </div>

                                                        {/* Content */}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center space-x-3 mb-2">
                                                                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                                                    {entry.title}
                                                                </h3>
                                                                {entry.favourite && !hideFavoriteIcon && (
                                                                    <div className="flex-shrink-0 p-1 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
                                                                        <StarSolidIcon className="w-4 h-4 text-yellow-500" />
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="flex items-center space-x-6">
                                                                {entry.username && (
                                                                    <div className="flex items-center space-x-2">
                                                                        <div className="p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                                                            <UserIcon className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                                                        </div>
                                                                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300 truncate max-w-40">
                                                                            {entry.username}
                                                                        </span>
                                                                    </div>
                                                                )}

                                                                {entry.website_url && (
                                                                    <div className="flex items-center space-x-2">
                                                                        <div className="p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                                                            <GlobeAltIcon className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                                                        </div>
                                                                        <span className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-48">
                                                                            {getDomainFromUrl(entry.website_url)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Categories */}
                                                            {entry.categories && entry.categories.length > 0 && (
                                                                <div className="mt-3">
                                                                    {renderCategories(entry.categories, categories)}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Strength Indicator */}
                                                        <div className="flex-shrink-0">
                                                            <PasswordStrengthIndicator
                                                                strength={calculatePasswordStrength(entry.password)}
                                                                showLabel={false}
                                                            />
                                                        </div>

                                                        {/* Actions */}
                                                        {/* <div className={`flex items-center space-x-1 transition-all duration-300 ${
                                                    hoveredEntry === entry.id ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                                                }`}>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCopyItem('username', entry.username);
                                                        }}
                                                        className="p-3 bg-white dark:bg-gray-700 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 
                                                                 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 
                                                                 shadow-md hover:shadow-lg border border-gray-200 dark:border-gray-600 group/btn"
                                                        title="Copy Username"
                                                    >
                                                        <UserIcon className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                                                    </button>
                                                    
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCopyItem('password', entry.password);
                                                        }}
                                                        className="p-3 bg-white dark:bg-gray-700 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 
                                                                 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 
                                                                 shadow-md hover:shadow-lg border border-gray-200 dark:border-gray-600 group/btn"
                                                        title="Copy Password"
                                                    >
                                                        <KeyIcon className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                                                    </button>
                                                    
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEdit(entry?.id || '', entry?.shared_key || '', 'write');
                                                        }}
                                                        className="p-3 bg-white dark:bg-gray-700 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 
                                                                 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 
                                                                 shadow-md hover:shadow-lg border border-gray-200 dark:border-gray-600 group/btn"
                                                        title="Edit Password"
                                                    >
                                                        <PencilIcon className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                                                    </button>
                                                </div> */}

                                                        <ChevronRightIcon className={`w-6 h-6 text-gray-400 flex-shrink-0 transition-all duration-300 ${selectedEntry?.id === entry.id ? 'rotate-90 text-primary-500' : 'group-hover:text-primary-400'
                                                            }`} />
                                                    </div>

                                                    {/* Hover effect overlay */}
                                                    <div className={`absolute inset-0 bg-gradient-to-r from-primary-500/5 to-primary-600/5 rounded-2xl transition-opacity duration-300 pointer-events-none ${hoveredEntry === entry.id ? 'opacity-100' : 'opacity-0'
                                                        }`} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Right Panel - Password Details */}
                                <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
                                    {selectedEntry ? (
                                        <div className="h-full p-6 overflow-y-auto">
                                            {/* Header */}
                                            <div className="flex items-center space-x-4 mb-8 pb-6 border-b border-gray-200 dark:border-gray-700">
                                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 
                                                      dark:from-primary-900/30 dark:to-primary-800/40 flex items-center justify-center 
                                                      shadow-lg border border-primary-200 dark:border-primary-700">
                                                    {selectedEntry.website_url || selectedEntry.favicon_url ? (
                                                        <img
                                                            src={getFaviconUrl(selectedEntry.website_url || '', selectedEntry.favicon_url || '')}
                                                            alt=""
                                                            className="w-10 h-10 rounded-xl"
                                                            onError={(e) => {
                                                                const target = e.target as HTMLImageElement;
                                                                target.src = '/default-favicon.png';
                                                            }}
                                                        />
                                                    ) : (
                                                        <KeyIcon className="w-10 h-10 text-primary-600 dark:text-primary-400" />
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                                        {selectedEntry.title}
                                                    </h2>
                                                    <div className="flex items-center space-x-4">
                                                        <PasswordStrengthIndicator
                                                            strength={calculatePasswordStrength(selectedEntry.password)}
                                                            showLabel={true}
                                                        />
                                                        {selectedEntry.favourite && !hideFavoriteIcon && (
                                                            <div className="flex items-center space-x-1 px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                                                                <StarSolidIcon className="w-4 h-4 text-yellow-500" />
                                                                <span className="text-xs font-medium text-yellow-700 dark:text-yellow-400">Favorite</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Details */}
                                            <div className="space-y-6">
                                                {/* Website URL */}
                                                {selectedEntry.website_url && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                            Website
                                                        </label>
                                                        <div className="flex items-center space-x-2">
                                                            <div className="flex-1 flex items-center px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl">
                                                                <GlobeAltIcon className="w-5 h-5 text-gray-400 mr-3" />
                                                                <input
                                                                    type="text"
                                                                    readOnly
                                                                    value={selectedEntry.website_url}
                                                                    className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none"
                                                                />
                                                            </div>
                                                            <button
                                                                onClick={() => window.open(selectedEntry.website_url, '_blank')}
                                                                className="p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-colors shadow-lg hover:shadow-xl"
                                                                title="Open website"
                                                            >
                                                                <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Username */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Username
                                                    </label>
                                                    <div className="flex items-center space-x-2">
                                                        <div className="flex-1 flex items-center px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl">
                                                            <UserIcon className="w-5 h-5 text-gray-400 mr-3" />
                                                            <input
                                                                type="text"
                                                                readOnly
                                                                value={selectedEntry.username}
                                                                className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none"
                                                            />
                                                        </div>
                                                        <button
                                                            onClick={() => navigator.clipboard.writeText(selectedEntry.username)}
                                                            className="p-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl transition-colors shadow-lg hover:shadow-xl"
                                                            title="Copy username"
                                                        >
                                                            <ClipboardDocumentIcon className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Password */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Password
                                                    </label>
                                                    <div className="flex items-center space-x-2">
                                                        <div className="flex-1 flex items-center px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl">
                                                            <LockClosedIcon className="w-5 h-5 text-gray-400 mr-3" />
                                                            <input
                                                                type={showPassword[selectedEntry.id] ? 'text' : 'password'}
                                                                readOnly
                                                                value={selectedEntry.password}
                                                                className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none font-mono"
                                                            />
                                                        </div>
                                                        <button
                                                            onClick={() => setShowPassword(prev => ({
                                                                ...prev,
                                                                [selectedEntry.id]: !prev[selectedEntry.id]
                                                            }))}
                                                            className="p-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl transition-colors shadow-lg hover:shadow-xl"
                                                            title="Toggle password visibility"
                                                        >
                                                            {showPassword[selectedEntry.id] ? (
                                                                <EyeSlashIcon className="w-5 h-5" />
                                                            ) : (
                                                                <EyeIcon className="w-5 h-5" />
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => navigator.clipboard.writeText(selectedEntry.password)}
                                                            className="p-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl transition-colors shadow-lg hover:shadow-xl"
                                                            title="Copy password"
                                                        >
                                                            <ClipboardDocumentIcon className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Categories */}
                                                {selectedEntry.categories && selectedEntry.categories.length > 0 && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                            Vaults
                                                        </label>
                                                        {renderCategories(selectedEntry.categories, categories)}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                                                <div className="flex justify-center space-x-4">
                                                    {/* Admin view - show View and Clone buttons */}
                                                    {isAdminView ? (
                                                        <>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEdit(selectedEntry?.id || '', selectedEntry?.shared_key || '', 'read');
                                                                }}
                                                                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors flex items-center justify-center shadow-lg hover:shadow-xl min-w-[120px]"
                                                            >
                                                                <EyeIcon className="h-4 w-4 mr-2" />
                                                                View
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (selectedEntry) {
                                                                        handleClone(selectedEntry);
                                                                        setShowDetailsPanel(false);
                                                                    }
                                                                }}
                                                                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors flex items-center justify-center shadow-lg hover:shadow-xl min-w-[120px]"
                                                            >
                                                                <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                                                                Clone
                                                            </button>
                                                        </>
                                                    ) : (
                                                        /* Normal view - show all actions based on permissions */
                                                        <>
                                                            {!selectedEntry?.is_owner && selectedEntry.permission_level == 'read' && role_id !== 1 && role_id !== 2 ? (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleEdit(selectedEntry?.id || '', selectedEntry?.shared_key || '', 'read');
                                                                    }}
                                                                    className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-colors flex items-center justify-center shadow-lg hover:shadow-xl min-w-[120px]"
                                                                >
                                                                    <EyeIcon className="h-4 w-4 mr-2" />
                                                                    View
                                                                </button>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleEdit(selectedEntry?.id || '', selectedEntry?.shared_key || '', 'write');
                                                                        }}
                                                                        className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-colors flex items-center justify-center shadow-lg hover:shadow-xl min-w-[120px]"
                                                                    >
                                                                        <PencilIcon className="h-4 w-4 mr-2" />
                                                                        Edit
                                                                    </button>
                                                                    {((selectedEntry?.is_owner) || (!selectedEntry?.is_owner)) && (
                                                                        <button
                                                                            onClick={() => handleDeleteClick(selectedEntry.id)}
                                                                            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors flex items-center justify-center shadow-lg hover:shadow-xl min-w-[120px]"
                                                                        >
                                                                            <TrashIcon className="h-4 w-4 mr-2" />
                                                                            Delete
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        // Default state - show first entry by default or empty state
                                        <div className="h-full flex items-center justify-center p-6">
                                            <div className="text-center">
                                                <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 
                                                      rounded-2xl flex items-center justify-center mb-6 shadow-lg mx-auto">
                                                    <KeyIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                                                </div>
                                                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">Select a Password</h3>
                                                <p className="text-gray-500 dark:text-gray-400">
                                                    Choose a password from the list to view its details
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Centered Details Panel - Only for Grid View */}
                    {selectedEntry && showDetailsPanel && viewMode === 'grid' && (
                        <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50">
                            <div
                                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="p-6">
                                    {/* Header */}
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="flex items-center space-x-3">
                                            <div className="flex-shrink-0">
                                                {selectedEntry.website_url ? (
                                                    <img
                                                        src={getFaviconUrl(selectedEntry.website_url, selectedEntry.favicon_url || '')}
                                                        alt=""
                                                        className="h-10 w-10 rounded-full"
                                                        onError={(e) => {
                                                            const target = e.target as HTMLImageElement;
                                                            target.src = '/default-favicon.png';
                                                        }}
                                                    />
                                                ) : (
                                                    <KeyIcon className="h-10 w-10 text-gray-400" />
                                                )}
                                            </div>
                                            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                                                {selectedEntry.title}
                                            </h2>
                                        </div>
                                        <button
                                            onClick={() => toggleDetailsPanel(selectedEntry.id)}
                                            className="text-gray-400 hover:text-gray-600"
                                        >
                                            <XMarkIcon className="h-6 w-6" />
                                        </button>
                                    </div>

                                    {/* Content */}
                                    <div className="space-y-6">
                                        {/* Website URL Section */}
                                        {selectedEntry.website_url && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Website
                                                </label>
                                                <div className="mt-1 flex items-center">
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        value={selectedEntry.website_url}
                                                        className="flex-1 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-lg text-sm"
                                                    />
                                                    <button
                                                        onClick={() => window.open(selectedEntry.website_url, '_blank')}
                                                        className="ml-2 p-2 text-gray-500 hover:text-gray-700"
                                                        title="Open website"
                                                    >
                                                        <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Username Section */}
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Username
                                            </label>
                                            <div className="mt-1 flex items-center">
                                                <input
                                                    type="text"
                                                    readOnly
                                                    value={selectedEntry.username}
                                                    className="flex-1 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-lg text-sm"
                                                />
                                                <button
                                                    onClick={() => navigator.clipboard.writeText(selectedEntry.username)}
                                                    className="ml-2 p-2 text-gray-500 hover:text-gray-700"
                                                >
                                                    <ClipboardDocumentIcon className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Password Section */}
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Password
                                            </label>
                                            <div className="mt-1 flex items-center">
                                                <input
                                                    type={showPassword[selectedEntry.id] ? 'text' : 'password'}
                                                    readOnly
                                                    value={selectedEntry.password}
                                                    className="flex-1 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-lg text-sm"
                                                />
                                                <button
                                                    onClick={() => setShowPassword(prev => ({
                                                        ...prev,
                                                        [selectedEntry.id]: !prev[selectedEntry.id]
                                                    }))}
                                                    className="ml-2 p-2 text-gray-500 hover:text-gray-700"
                                                >
                                                    {showPassword[selectedEntry.id] ? (
                                                        <EyeSlashIcon className="h-5 w-5" />
                                                    ) : (
                                                        <EyeIcon className="h-5 w-5" />
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => navigator.clipboard.writeText(selectedEntry.password)}
                                                    className="ml-2 p-2 text-gray-500 hover:text-gray-700"
                                                >
                                                    <ClipboardDocumentIcon className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Vaults/Categories Section */}
                                        {selectedEntry.categories && selectedEntry.categories.length > 0 && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                                                    Vaults ({selectedEntry.categories.length})
                                                </label>
                                                <div className="flex flex-wrap gap-2">
                                                    {selectedEntry.categories.map((category, idx) => {
                                                        // Find matching category from available categories
                                                        const matchedCategory = categories.find((cat: any) => cat.id === category.id);
                                                        if (!matchedCategory) return null;
                                                        
                                                        return (
                                                            <span
                                                                key={category.id}
                                                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 border border-primary-200 dark:border-primary-700/50 hover:bg-primary-200 dark:hover:bg-primary-800/40 transition-colors"
                                                                title={matchedCategory.description || matchedCategory.name}
                                                            >
                                                                <FolderIcon className="w-3 h-3 mr-1.5" />
                                                                {matchedCategory.name}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Action Buttons */}
                                        <div className="flex space-x-3 mt-6">
                                            {isAdminView ? (
                                                <>
                                                    <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEdit(selectedEntry?.id || '', selectedEntry?.shared_key || '', 'read');
                                                                }}
                                                                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors flex items-center justify-center shadow-lg hover:shadow-xl min-w-[120px]"
                                                            >
                                                                <EyeIcon className="h-4 w-4 mr-2" />
                                                                View
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (selectedEntry) {
                                                                        handleClone(selectedEntry);
                                                                        setShowDetailsPanel(false);
                                                                    }
                                                                }}
                                                                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors flex items-center justify-center shadow-lg hover:shadow-xl min-w-[120px]"
                                                            >
                                                                <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                                                                Clone
                                                            </button>

                                                    
                                                </>
                                            ): (
                                                <>

                                            {!selectedEntry?.is_owner && selectedEntry.permission_level == 'read' && role_id !== 1 && role_id !== 2 ?
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEdit(selectedEntry?.id || '', selectedEntry?.shared_key || '', 'read');
                                                    }}
                                                    className="flex-1 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center"
                                                >
                                                    <EyeIcon className="h-5 w-5 mr-2" />
                                                    View
                                                </button>
                                                :
                                                <>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEdit(selectedEntry?.id || '', selectedEntry?.shared_key || '', 'write');
                                                        }}
                                                        className="flex-1 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center"
                                                    >
                                                        <PencilIcon className="h-5 w-5 mr-2" />
                                                        Edit
                                                    </button>
                                                    {((selectedEntry?.is_owner) || (!selectedEntry?.is_owner)) && (
                                                        <button
                                                            onClick={() => handleDeleteClick(selectedEntry.id)}
                                                            className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center"
                                                        >
                                                            <TrashIcon className="h-5 w-5 mr-2" />
                                                            Delete
                                                        </button>
                                                    )}
                                                </>
                                            }
                                            </>
                                            )}

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Edit Modal */}
                    {selectedEntry && isEditModalOpen && (
                        <EditPasswordModal
                            entry={selectedEntry}
                            isOpen={isEditModalOpen}
                            onClose={() => {
                                setIsEditModalOpen(false);
                                closeDetailsPanel();
                            }}
                            isSharedPassword={selectedEntry.shared_key ? true : isShared}
                            permissionLevel={isAdminView ? 'read' : role_id === 1 || role_id === 2 ? 'write' : passwordPermissionLevel}
                            onSuccessfulUpdate={onSuccessfulUpdate}
                        />
                    )}

                </>
            )}


            {/* Add ConfirmModal at the end of the component */}
            <ConfirmModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmAction}
                title={actionType === 'delete' ? 'Delete Password' : 'Remove from Category'}
                message={actionType === 'delete'
                    ? 'Are you sure you want to delete this password? This action cannot be undone.'
                    : 'Are you sure you want to remove this password from its category?'}
                confirmText={actionType === 'delete' ? 'Delete' : 'Remove'}
                isLoading={isProcessing}
            />
        </div>
    );
};

export default PasswordList;